import { memo, useMemo } from 'react';
import { Link } from 'react-router';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { NavRoute } from './types';
import {
  getListItemButtonStyles,
  getListItemIconStyles,
  getListItemStyles,
  getListItemTextStyles,
} from './styles';
import { SystemStyleObject } from '@mui/system';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

export interface NavItemProps extends NavRoute {
  expandedView: boolean;
  overridingListItemStyles?: SystemStyleObject<Theme>;
  onClick?: () => void;
}

function NavItemBase({
  to,
  isActive,
  title,
  Icon,
  external,
  href,
  expandedView,
  overridingListItemStyles,
  onClick,
}: NavItemProps) {
  const listItemStyles = useMemo(
    () => getListItemStyles(expandedView, overridingListItemStyles),
    [expandedView, overridingListItemStyles]
  );
  const listItemButtonStyles = useMemo(() => getListItemButtonStyles(isActive), [isActive]);
  const listItemIconStyles = useMemo(() => getListItemIconStyles(isActive), [isActive]);
  const listItemTextStyles = useMemo(() => getListItemTextStyles(expandedView), [expandedView]);

  return (
    <ListItem disablePadding sx={listItemStyles}>
      <Tooltip title={expandedView ? '' : title} placement="right" arrow>
        <ListItemButton
          to={to}
          href={href}
          component={external ? 'a' : Link}
          onClick={onClick}
          selected={isActive}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          sx={listItemButtonStyles}
        >
          <ListItemIcon sx={listItemIconStyles}>
            <Icon fontSize="small" />
          </ListItemIcon>

          <ListItemText
            disableTypography
            primary={
              <Typography
                sx={{
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start',
                  gap: 1,
                }}
              >
                {title} {external && <OpenInNewIcon fontSize="small" />}
              </Typography>
            }
            sx={listItemTextStyles}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}

export const NavItem = memo(NavItemBase);
